import { supabase } from './supabase'

export type LogLevel = 'debug' | 'info' | 'warn' | 'error'

export interface LogEntry {
  level: LogLevel
  message: string
  data?: Record<string, unknown>
  timestamp: string
  url?: string
  userAgent?: string
  userId?: string | null
}

const LEVEL_PREFIX: Record<LogLevel, string> = {
  debug: '[DEBUG]',
  info: '[INFO]',
  warn: '[WARN]',
  error: '[ERROR]',
}

const LEVEL_COLORS: Record<LogLevel, string> = {
  debug: 'color: #6b7280',
  info: 'color: #3b82f6',
  warn: 'color: #f59e0b',
  error: 'color: #ef4444; font-weight: bold',
}

let currentUserId: string | null = null
let sessionLog: LogEntry[] = []
const MAX_SESSION_LOG = 200
let errorBatch: LogEntry[] = []
let batchTimer: ReturnType<typeof setTimeout> | null = null
const FLUSH_INTERVAL = 5000
const MAX_BATCH_SIZE = 25

export function setLoggerUserId(id: string | null) {
  currentUserId = id
}

function buildEntry(level: LogLevel, message: string, data?: Record<string, unknown>): LogEntry {
  return {
    level,
    message,
    data,
    timestamp: new Date().toISOString(),
    url: typeof window !== 'undefined' ? window.location.href : undefined,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    userId: currentUserId,
  }
}

function consoleLog(entry: LogEntry) {
  const prefix = LEVEL_PREFIX[entry.level]
  const label = `%c${prefix}%c ${entry.message}`
  const style = LEVEL_COLORS[entry.level]
  if (entry.data && Object.keys(entry.data).length > 0) {
    console.log(label, style, '', entry.data)
  } else {
    console.log(label, style, '')
  }
}

function addToSession(entry: LogEntry) {
  sessionLog.push(entry)
  if (sessionLog.length > MAX_SESSION_LOG) {
    sessionLog = sessionLog.slice(-MAX_SESSION_LOG)
  }
}

let tableExists: boolean | null = null

async function flushErrorBatch() {
  if (errorBatch.length === 0) return
  if (tableExists === false) return

  const batch = errorBatch.splice(0, MAX_BATCH_SIZE)
  try {
    if (tableExists === null) {
      const { error: checkError } = await supabase.from('client_errors').select('id', { count: 'exact', head: true }).limit(1)
      if (checkError && (checkError.code === '42P01' || checkError.message?.includes('does not exist') || checkError.message?.includes('schema cache'))) {
        tableExists = false
        return
      }
      tableExists = true
    }

    const { error } = await supabase.from('client_errors').insert(
      batch.map(e => ({
        level: e.level,
        message: e.message,
        data: e.data ? JSON.stringify(e.data) : null,
        url: e.url,
        user_agent: e.userAgent,
        user_id: e.userId,
      }))
    )
    if (error) {
      if (error.message?.includes('does not exist') || error.message?.includes('schema cache')) {
        tableExists = false
        return
      }
      console.warn('[LOGGER] Failed to flush error batch:', error.message)
    }
  } catch (err) {
    console.warn('[LOGGER] Failed to flush error batch:', err instanceof Error ? err.message : err)
  }
}

function queueError(entry: LogEntry) {
  errorBatch.push(entry)
  if (errorBatch.length >= MAX_BATCH_SIZE) {
    if (batchTimer) clearTimeout(batchTimer)
    batchTimer = null
    flushErrorBatch()
  } else if (!batchTimer) {
    batchTimer = setTimeout(() => {
      batchTimer = null
      flushErrorBatch()
    }, FLUSH_INTERVAL)
  }
}

function log(level: LogLevel, message: string, data?: Record<string, unknown>) {
  const entry = buildEntry(level, message, data)
  consoleLog(entry)
  addToSession(entry)
  if (level === 'error' || level === 'warn') {
    queueError(entry)
  }
}

const logger = {
  debug: (message: string, data?: Record<string, unknown>) => log('debug', message, data),
  info: (message: string, data?: Record<string, unknown>) => log('info', message, data),
  warn: (message: string, data?: Record<string, unknown>) => log('warn', message, data),
  error: (message: string, data?: Record<string, unknown>) => log('error', message, data),

  captureException: (err: unknown, context?: Record<string, unknown>) => {
    const message = err instanceof Error ? err.message : String(err)
    const stack = err instanceof Error ? err.stack : undefined
    log('error', message, { ...context, stack, errorType: err instanceof Error ? err.constructor.name : typeof err })
  },

  getSessionLog: () => [...sessionLog],

  flush: async () => {
    if (batchTimer) {
      clearTimeout(batchTimer)
      batchTimer = null
    }
    await flushErrorBatch()
  },
}

export function initGlobalErrorHandler() {
  if (typeof window === 'undefined') return

  window.addEventListener('unhandledrejection', (event) => {
    logger.captureException(event.reason, { type: 'unhandledrejection' })
  })

  window.addEventListener('error', (event) => {
    logger.captureException(event.error || event.message, {
      type: 'window.error',
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    })
  })

  const originalConsoleError = console.error
  console.error = (...args: unknown[]) => {
    const message = args.map(a => (typeof a === 'object' ? tryStringify(a) : String(a))).join(' ')
    logger.error(message, { source: 'console.error', args: args.map(a => typeof a) })
    originalConsoleError.apply(console, args)
  }

  logger.info('Global error handlers initialized')
}

function tryStringify(obj: unknown): string {
  try {
    return JSON.stringify(obj)
  } catch {
    return String(obj)
  }
}

export { logger }
